/**
 * /projects/:id/report — générer, relire, confirmer un rapport (chunk 9.AI.1).
 *
 * ## Le parcours, et rien de plus
 *
 *   générer → prévisualiser → vérifier → confirmer
 *
 * Pas de workflow éditorial : aucune révision, aucun commentaire, aucun envoi.
 * Le conseiller produit un document, le relit, et engage son nom dessus.
 *
 * ## Deux audiences, un seul rendu
 *
 * L'audience change ce que l'API PROJETTE, pas ce que le composant décide. La
 * version client n'a aucun chemin pour afficher un identifiant ou un statut
 * technique : ils ne sont pas dans la charge.
 *
 * ## Confirmé n'est pas figé, et l'écran le dit
 *
 * La base ne copie aucune valeur comparée : le contenu se reconstruit à chaque
 * lecture. Un rapport confirmé dont les sources ont bougé depuis l'affiche
 * explicitement. Le taire laisserait croire à une archive, et ce n'en est pas
 * une — l'export PDF est l'impression du navigateur, pas un document scellé.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { comparisonContext } from "../../lib/comparisonNavigation";
import {
  useComparisonReport,
  useComparisonReports,
  useConfirmComparisonReport,
  useGenerateComparisonReport,
  type ComparisonReportPayload,
} from "../../lib/queries/comparisonReports";

import { ReportDocument } from "./_parts/ReportDocument";

type Audience = "advisor" | "client";
type Langue = "fr" | "en";

const AUDIENCES: Audience[] = ["advisor", "client"];
const LANGUES: Langue[] = ["fr", "en"];

/** Deux boutons radio déguisés : un choix, pas deux interrupteurs indépendants. */
function ChoixSegmente<T extends string>({
  legende,
  valeurs,
  valeur,
  onChange,
  libelle,
}: {
  legende: string;
  valeurs: readonly T[];
  valeur: T;
  onChange: (v: T) => void;
  libelle: (v: T) => string;
}) {
  return (
    <fieldset className="min-w-0">
      <legend className="mb-1 text-xs font-medium text-slate-500">{legende}</legend>
      <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
        {valeurs.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={v === valeur}
            onClick={() => onChange(v)}
            className={[
              "min-w-max flex-auto touch-manipulation rounded-md px-2 py-2 text-sm font-medium transition-colors",
              "focus-visible:ring-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
              v === valeur ? "bg-brand-600 text-white" : "text-slate-700 hover:bg-slate-50",
            ].join(" ")}
          >
            {libelle(v)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function Banniere({
  ton,
  titre,
  children,
}: {
  ton: "amber" | "slate" | "emerald";
  titre: string;
  children?: React.ReactNode;
}) {
  const tons = {
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  } as const;
  return (
    <div role="status" className={`mb-3 rounded-xl border px-4 py-3 text-sm ${tons[ton]}`}>
      <p className="font-medium">{titre}</p>
      {children ? <p className="mt-1 max-w-prose">{children}</p> : null}
    </div>
  );
}

export function ProjectReportPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);

  // L'état vit dans l'URL : un rapport client en anglais doit pouvoir se
  // transmettre par lien, et un rechargement ne doit pas ramener au réglage par
  // défaut au milieu d'une relecture.
  const [params, setParams] = useSearchParams();
  const audience = (params.get("audience") === "client" ? "client" : "advisor") as Audience;
  const langue = (params.get("lang") === "en" ? "en" : "fr") as Langue;

  const [charge, setCharge] = useState<ComparisonReportPayload | null>(null);

  const liste = useComparisonReports(id);
  const generer = useGenerateComparisonReport();
  const confirmer = useConfirmComparisonReport();

  /**
   * Le rapport existant pour ce couple audience/langue : le brouillon vivant s'il
   * y en a un, sinon la confirmation la plus récente.
   *
   * Sans cette recherche, recharger après une confirmation n'offrirait qu'un
   * geste — régénérer — et créerait un brouillon là où le conseiller voulait
   * relire ce qu'il a confirmé.
   */
  const existant = useMemo(() => {
    const candidats = (liste.data ?? []).filter(
      (r) => r.audience === audience && r.language === langue,
    );
    return candidats.find((r) => r.confirmed_at === null) ?? candidats[0] ?? null;
  }, [liste.data, audience, langue]);

  const relecture = useComparisonReport(charge === null ? existant?.id : undefined);

  // La charge affichée vient soit de la génération qu'on vient de faire, soit de
  // la relecture — reconstruite depuis les sources courantes, jamais depuis une
  // copie stockée.
  const affiche = charge ?? relecture.data ?? null;

  // Changer d'audience ou de langue change de document : garder l'ancien à
  // l'écran ferait relire un rapport pour l'autre destinataire.
  useEffect(() => {
    setCharge(null);
  }, [audience, langue]);

  const majParam = useCallback(
    (cle: string, valeur: string) => {
      const suivant = new URLSearchParams(params);
      suivant.set(cle, valeur);
      setParams(suivant, { replace: true });
    },
    [params, setParams],
  );

  const onGenerer = useCallback(() => {
    if (!id) return;
    generer.mutate(
      { project_id: id, audience, language: langue },
      { onSuccess: (data) => setCharge(data) },
    );
  }, [id, audience, langue, generer]);

  const onConfirmer = useCallback(() => {
    const rapportId = affiche?.report.id;
    if (!rapportId) return;
    confirmer.mutate(rapportId, { onSuccess: (data) => setCharge(data) });
  }, [affiche, confirmer]);

  const erreur = generer.error ?? confirmer.error ?? relecture.error ?? liste.error;
  const texteErreur =
    erreur instanceof ApiException
      ? getApiErrorMessage(erreur, lang) || t("errors.generic")
      : erreur
        ? t("errors.generic")
        : null;

  const enCours = generer.isPending || confirmer.isPending;
  const confirme = affiche?.report.confirmed_at != null;
  const dateConfirmation = affiche?.report.confirmed_at
    ? new Intl.DateTimeFormat(lang === "en" ? "en-CA" : "fr-CA", {
        dateStyle: "long",
        timeStyle: "short",
      }).format(new Date(affiche.report.confirmed_at))
    : null;

  return (
    <div className="mx-auto w-full max-w-none">
      {/* Tout ce bloc disparaît à l'impression : un document remis à un client ne
          porte ni bouton ni fil d'ariane. */}
      <header className="mb-4 border-b border-slate-200 pb-3 print:hidden">
        <Link
          to={`/projects/${id}/comparison${comparisonContext(params)}`}
          className="focus-visible:ring-brand-500 text-sm text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("report.back_to_comparison")}
        </Link>
        <h1 className="mt-1 text-balance text-xl font-semibold text-slate-900">
          {t("report.title")}
        </h1>
        <p className="mt-2 text-sm font-medium text-slate-700">
          {t("reception_flow.project_scope")}
        </p>
        <details className="mt-1 text-sm text-slate-600">
          <summary className="focus-visible:ring-brand-500 w-fit cursor-pointer rounded py-1 focus-visible:outline-none focus-visible:ring-2">
            {t("report.help")}
          </summary>
          <p className="mt-1 max-w-prose">{t("report.subtitle")}</p>
        </details>

        <div className="mt-3 grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
          <ChoixSegmente
            legende={t("report.audience.legend")}
            valeurs={AUDIENCES}
            valeur={audience}
            onChange={(v) => majParam("audience", v)}
            libelle={(v) => t(`report.audience.${v}`)}
          />
          <ChoixSegmente
            legende={t("report.language.legend")}
            valeurs={LANGUES}
            valeur={langue}
            onChange={(v) => majParam("lang", v)}
            libelle={(v) => t(`report.language.${v}`)}
          />

          <div className="col-span-2 flex flex-wrap gap-2 sm:ml-auto">
            <button
              type="button"
              onClick={onGenerer}
              disabled={enCours}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60"
            >
              {generer.isPending
                ? t("report.actions.generating")
                : affiche
                  ? t("report.actions.regenerate")
                  : t("report.actions.generate")}
            </button>

            {affiche && !confirme ? (
              <button
                type="button"
                onClick={onConfirmer}
                disabled={enCours}
                className="touch-manipulation rounded-lg border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-colors hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-60"
              >
                {confirmer.isPending ? t("report.actions.confirming") : t("report.actions.confirm")}
              </button>
            ) : null}

            {affiche ? (
              <button
                type="button"
                onClick={() => window.print()}
                className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                {t("report.actions.print")}
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <div aria-live="polite" className="print:hidden">
        {texteErreur ? (
          <div
            role="alert"
            className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {texteErreur}
          </div>
        ) : null}

        {enCours ? (
          <p className="mb-3 text-sm text-slate-500">{t("report.states.working")}</p>
        ) : null}

        {/* L'avertissement AVANT le partage. Le masquer reviendrait à laisser
            partir un document dont une ligne n'est pas vérifiée. */}
        {affiche && affiche.projection.needs_review_count > 0 ? (
          <Banniere ton="amber" titre={t("report.warnings.needs_review_title")}>
            {t("report.warnings.needs_review_body", {
              count: affiche.projection.needs_review_count,
            })}
          </Banniere>
        ) : null}

        {affiche && affiche.projection.has_human_overrides && audience === "advisor" ? (
          <Banniere ton="slate" titre={t("report.warnings.overrides_title")}>
            {t("report.warnings.overrides_body")}
          </Banniere>
        ) : null}

        {confirme ? (
          <Banniere
            ton="emerald"
            titre={t("report.confirmed.title", {
              name: affiche?.report.confirmed_by_display_name ?? t("report.confirmed.unknown_user"),
              date: dateConfirmation ?? "",
            })}
          >
            {t("report.confirmed.not_an_archive")}
          </Banniere>
        ) : null}

        {/* Les sources ont bougé depuis la génération. C'est la conséquence
            assumée de « pas de copie » — la dire vaut mieux que la taire. */}
        {affiche?.sources_changed ? (
          <Banniere ton="amber" titre={t("report.warnings.sources_changed_title")}>
            {t("report.warnings.sources_changed_body")}
          </Banniere>
        ) : null}
      </div>

      {affiche ? (
        <ReportDocument projection={affiche.projection} language={langue} />
      ) : (
        !enCours && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center print:hidden">
            <h2 className="text-base font-semibold text-slate-900">{t("report.empty.title")}</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
              {t("report.empty.subtitle")}
            </p>
          </div>
        )
      )}
    </div>
  );
}

export default ProjectReportPage;
